import { useState, useEffect } from 'react';
import { Download, RefreshCw, X } from 'lucide-react';

/**
 * UpdateBanner — Shows a dismissible banner when an Electron auto-update is available.
 * Only renders in the desktop (Electron) context.
 */
export function UpdateBanner() {
    const [updateInfo, setUpdateInfo] = useState<{ version?: string } | null>(null);
    const [downloaded, setDownloaded] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        const electronAPI = (window as any).electronAPI;
        if (!electronAPI) return; // Web app — skip

        const unsub1 = electronAPI.onUpdateAvailable?.((info: { version?: string }) => {
            setUpdateInfo(info);
        });

        const unsub2 = electronAPI.onUpdateDownloaded?.((info: { version?: string }) => {
            setUpdateInfo(info);
            setDownloaded(true);
        });

        return () => {
            unsub1?.();
            unsub2?.();
        };
    }, []);

    if (!updateInfo || dismissed) return null;

    const handleInstall = () => {
        (window as any).electronAPI?.installUpdate();
    };

    return (
        <div style={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 9999,
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            maxWidth: 360,
            animation: 'slideUp 0.3s ease'
        }}>
            <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'rgba(var(--accent-rgb), 0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0
            }}>
                {downloaded
                    ? <RefreshCw size={18} style={{ color: 'var(--accent)' }} />
                    : <Download size={18} style={{ color: 'var(--accent)' }} />
                }
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {downloaded ? 'Update ready to install' : 'Update downloading…'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {updateInfo.version ? `v${updateInfo.version}` : 'New version available'}
                    {downloaded ? ' — restart to apply' : ''}
                </div>
            </div>

            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {downloaded && (
                    <button
                        onClick={handleInstall}
                        className="btn btn-primary"
                        style={{ fontSize: 12, padding: '4px 10px' }}
                    >
                        Restart
                    </button>
                )}
                <button
                    onClick={() => setDismissed(true)}
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--text-muted)', padding: 4, display: 'flex',
                        alignItems: 'center', borderRadius: 4
                    }}
                    title="Dismiss"
                >
                    <X size={14} />
                </button>
            </div>
        </div>
    );
}
